import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Landmark, ShieldCheck } from "lucide-react";
import { Layout } from "../components/Layout";

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "About the Cabinet · CurioVault";
    return () => { document.title = "CurioVault"; };
  }, []);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="about-us-page">
        <nav className="flex items-center gap-2 font-mono-cv text-xs text-[#7A6C5D] mb-8" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#9E4535]">Vault</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1A1615]">About</span>
        </nav>

        <div className="flex items-center gap-4 mb-2">
          <div className="w-12 h-12 shrink-0 rounded-full border-2 border-[#C5A059] flex items-center justify-center text-[#9E4535]">
            <Landmark className="w-6 h-6" />
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1615] leading-tight">
            About the Cabinet
          </h1>
        </div>
        <p className="caption-label mb-8 ml-16">Est. MMXXVI · London, England</p>

        <div className="bg-white border border-[#D8CEB9] rounded-sm p-7 sm:p-9 space-y-5">
          <p className="font-body text-base sm:text-lg text-[#2E282A] leading-[1.85] drop-cap">
            CurioVault is a private cabinet of curiosities, assembled for the endlessly
            inquisitive.
          </p>
          <p className="font-body text-base sm:text-lg text-[#2E282A] leading-[1.85]">
            We catalogue verified oddities from the natural world, forgotten history, the
            human body, and the far reaches of the universe. Every curio is checked against
            primary sources, academic records, and contemporary reporting.
          </p>
          <p className="font-body text-base sm:text-lg text-[#2E282A] leading-[1.85]">
            This is not an aggregator. Each entry is researched, written, and illustrated
            in-house and filed by subject, as a museum would file its specimens.
          </p>
          <p className="font-body text-base sm:text-lg text-[#2E282A] leading-[1.85]">
            CurioVault was founded in 2026 in London, England, with a simple principle:
            the world is stranger than fiction, and it deserves a proper vault.
          </p>
          <p className="font-body italic text-[#9E4535] border-l-4 border-[#C5A059] pl-5">
            Curated by the CurioVault team.
          </p>
        </div>

        {/* Credibility Promise */}
        <div className="mt-8 bg-white border border-[#D8CEB9] rounded-sm p-7 sm:p-9" data-testid="credibility-promise">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 shrink-0 rounded-full border-2 border-[#C5A059] flex items-center justify-center text-[#9E4535]">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h2 className="font-serif-display text-2xl font-bold text-[#1A1615]">Our Credibility Promise</h2>
          </div>
          <p className="font-body text-base sm:text-lg text-[#2E282A] leading-[1.85]">
            Every Curio is fact-checked against reputable sources like Wikipedia, Britannica,
            the Smithsonian, NASA, and peer-reviewed journals. We verify primary sources for a
            live (200-OK) response where possible, and maintain a second reputable source for depth.
          </p>
          <div className="flex flex-wrap gap-2 mt-5">
            {["Wikipedia", "Britannica", "Smithsonian", "NASA", "NIH / PNAS", "BBC"].map((s) => (
              <span key={s} className="font-mono-cv text-[11px] tracking-wide uppercase text-[#4A4036] bg-[#F3EDE0] border border-[#D8CEB9] rounded-full px-3 py-1">
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-[#F3EDE0] border border-[#D8CEB9] rounded-sm p-6">
          <p className="caption-label mb-2">Contact</p>
          <a
            href="mailto:curator@curiovault.co"
            data-testid="about-contact-email"
            className="font-ui text-sm text-[#9E4535] hover:underline"
          >
            curator@curiovault.co
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default AboutUs;
